import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Tooltip,
  Snackbar,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  DeleteOutline as DeleteIcon,
  HelpOutline as HelpIcon,
  OpenInNew as OpenInNewIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  LockOutlined as LockIcon,
  VerifiedUser as VerifiedUserIcon,
  Schedule as ScheduleIcon,
  Autorenew as AutorenewIcon,
  TaskAlt as TaskAltIcon,
  AddCircleOutline as AddIcon,
  LinkOff as DisconnectIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { credentialsApi, adminApi, featuresApi, onboardingApi, socialAccountsApi } from '../services/api';
import { auth } from '../firebase';
import { PLATFORM_CONFIG, PLATFORM_ICONS } from './post/PLATFORM_CONFIG';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { PLATFORM_FEATURES } from '../constants/features';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// ─── Developer & Callback Info ──────────────────────────────────────────────

const DeveloperInfo = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState('');
  const frontendUrl = window.location.origin;
  const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const info = [
    { label: 'YouTube Redirect URI', value: `${frontendUrl}/settings` },
    { label: 'Facebook Redirect URI', value: `${frontendUrl}/settings` },
    { label: 'Instagram Redirect URI', value: `${frontendUrl}/settings` },
    { label: 'Instagram Webhook URL', value: `${backendUrl}/webhook/instagram` },
    { label: 'Facebook Webhook URL', value: `${backendUrl}/webhook/facebook` },
  ];

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <Accordion
      elevation={0}
      sx={{
        mb: 4,
        borderRadius: '6px !important',
        '&:before': { display: 'none' },
        background: isDark ? alpha('#6366f1', 0.06) : alpha('#6366f1', 0.03),
        border: `1px dashed ${alpha('#6366f1', 0.3)}`,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#6366f1' }} />}
        sx={{
          borderRadius: '6px',
          minHeight: 48,
          px: 2.5,
          '&.Mui-expanded': { borderRadius: '6px 6px 0 0', minHeight: 48 },
          '& .MuiAccordionSummary-content': { my: 1.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#6366f1', fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }}>
            Developer & Callback Information
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Use these URLs when configuring your apps in the Google, Meta, and other developer portals.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {info.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.7),
                p: 1, px: 2, borderRadius: '6px',
                border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#000', 0.05)}`,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary' }}>{item.label}</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, wordBreak: 'break-all', color: 'text.primary' }}>{item.value}</Typography>
              </Box>
              <Button
                size="small"
                startIcon={copied === item.label ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                onClick={() => handleCopy(item.value, item.label)}
                sx={{
                  ml: 2, minWidth: 80, textTransform: 'none', fontWeight: 700, borderRadius: '4px',
                  color: copied === item.label ? '#10b981' : '#6366f1',
                }}
              >
                {copied === item.label ? 'Copied' : 'Copy'}
              </Button>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Step-by-step credential guides ──────────────────────────────────────────

// Each step is either a plain string, or { text, link, linkLabel }
// Steps with a `link` will render a clickable "Open ↗" button inline.
const PLATFORM_GUIDES = {
  youtube: {
    title: 'How to connect YouTube',
    sections: [
      {
        heading: 'Find your YouTube Channel ID (optional)',
        steps: [
          { text: 'Go to YouTube Studio, then click Settings (gear icon, bottom-left) → Channel → Basic info tab.', link: 'https://studio.youtube.com', linkLabel: 'Open YouTube Studio' },
          'Your Channel ID starts with "UC" followed by 22 characters — copy it and paste it into the Channel ID field.',
          'The Channel ID is optional but recommended so the app can target your specific channel if your account manages multiple channels.',
        ],
      },
      {
        heading: 'Connect via OAuth',
        steps: [
          'Click the "Connect to YouTube" button below.',
          'You\'ll be taken to Google\'s sign-in page. Choose the account that owns your YouTube channel and click Allow.',
          'You\'ll be sent back here automatically. When you see "Successfully connected to YouTube!" you\'re all set.',
          '⚡ The connection uses the platform\'s shared OAuth app — no developer account needed.',
        ],
      },
    ],
  },
  instagram: {
    title: 'How to connect Instagram',
    sections: [
      {
        heading: 'Prerequisites — do these first',
        steps: [
          'Your Instagram account must be a Business or Creator account (not personal).\n  → Open the Instagram app → tap your profile → hamburger menu (☰) → Settings and Privacy → Account type and tools → Switch to Professional Account → choose Business or Creator.',
          'Your Instagram Business/Creator account must be linked to a Facebook Page you manage.\n  → Instagram app → Settings and Privacy → Account Center → Profiles → Add Instagram → follow the prompts to link to your Facebook Page.\n  → Alternatively: Facebook → your Page → Settings → Linked Accounts → Instagram.',
          'You must be the admin of both the Instagram account and the Facebook Page.',
        ],
      },
      {
        heading: 'Connect via OAuth',
        steps: [
          'Click the "Connect to Instagram" button below.',
          'You\'ll be redirected to Facebook\'s OAuth dialog. Log in with the Facebook account that manages your linked Facebook Page and approve all requested permissions.',
          'You\'ll be redirected back automatically. The app finds your linked Instagram Business Account and stores the token securely.',
          'You will see "Instagram @yourusername connected successfully!" when complete.',
          '⚡ Token lifespan: long-lived tokens last 60 days. The app will remind you to reconnect when the token is close to expiry.',
        ],
      },
      {
        heading: 'Troubleshooting',
        steps: [
          '"No Instagram Business or Creator account found" — your Instagram account is not linked to a Facebook Page, or it is still a personal account. Redo the Prerequisites step above.',
          '"Connection failed" — try clicking Connect again. If it persists, contact your admin as the platform OAuth app may need re-configuration.',
        ],
      },
    ],
  },
  facebook: {
    title: 'How to connect Facebook',
    sections: [
      {
        heading: 'Prerequisites — do these first',
        steps: [
          'You must have a Facebook Page (not just a personal profile). If you don\'t have one: facebook.com → Pages → Create new Page.',
          'You must be an Admin of that Facebook Page.',
        ],
      },
      {
        heading: 'Connect via OAuth',
        steps: [
          'Click the "Connect to Facebook" button below.',
          'You\'ll be redirected to Facebook\'s OAuth dialog. Log in with the Facebook account that manages your Page and approve all requested permissions. Make sure you select the correct Page when prompted.',
          'You\'ll be redirected back automatically. The app exchanges the code for a permanent Page access token.',
          'You will see "Connected to Facebook Page \'Your Page Name\'" when complete.',
          '⚡ Token lifespan: Page access tokens obtained this way do NOT expire unless you change your password or explicitly revoke the app.',
        ],
      },
      {
        heading: 'Troubleshooting',
        steps: [
          '"No Facebook Pages found" — you approved the dialog but did not select a Page, or you don\'t manage any Pages. Try connecting again and tick the Page checkbox during the OAuth dialog.',
          '"Connection failed" — contact your admin as the platform OAuth app may need re-configuration.',
        ],
      },
    ],
  },
  twitter: {
    title: 'How to connect Twitter / X',
    sections: [
      {
        heading: 'Apply for developer access',
        steps: [
          { text: 'Go to the Twitter Developer Portal and sign up for a developer account if you haven\'t already.', link: 'https://developer.twitter.com/en/portal/dashboard', linkLabel: 'Open Developer Portal' },
          'Answer the use-case questions — approval is usually instant for basic (free) access.',
        ],
      },
      {
        heading: 'Create a Project and App',
        steps: [
          { text: 'Inside the Developer Portal, click "+ New Project", give it a name, and select a use case.', link: 'https://developer.twitter.com/en/portal/projects-and-apps', linkLabel: 'Open Projects & Apps' },
          'Create a new App inside the project.',
          'Go to your App Settings → "User authentication settings" → enable OAuth 1.0a.',
          'Set App permissions to "Read and Write" — this is required for posting.',
        ],
      },
      {
        heading: 'Copy your credentials',
        steps: [
          'In the "Keys and Tokens" tab, copy your API Key (Consumer Key) and API Key Secret.',
          'Click "Generate" under "Access Token and Secret" — copy both values immediately (they\'re only shown once).',
          'Copy the Bearer Token shown at the top of the same page.',
          'All five values go into the fields below.',
        ],
      },
    ],
  },
  linkedin: {
    title: 'How to connect LinkedIn',
    sections: [
      {
        heading: 'Create a LinkedIn Developer App',
        steps: [
          { text: 'Go to LinkedIn Developer Apps and click "Create app".', link: 'https://www.linkedin.com/developers/apps', linkLabel: 'Open LinkedIn Apps' },
          'Fill in the app name, link it to a LinkedIn Company Page (required), upload a logo, then click Create App.',
        ],
      },
      {
        heading: 'Add required products',
        steps: [
          'In your app, click the "Products" tab.',
          'Request access to "Share on LinkedIn" and "Sign In with LinkedIn using OpenID Connect". Most apps get instant approval.',
        ],
      },
      {
        heading: 'Get an access token',
        steps: [
          { text: 'Go to the LinkedIn OAuth Token Generator tool to get a token without writing any code.', link: 'https://www.linkedin.com/developers/tools/oauth/token-generator', linkLabel: 'Open Token Generator' },
          'Select your app, tick the scopes r_liteprofile and w_member_social, then click "Request access token".',
          'Sign in with your LinkedIn account when prompted.',
          'Copy the access token shown — it is valid for 60 days. Paste it into the Access Token field below.',
        ],
      },
    ],
  },
  reddit: {
    title: 'How to connect Reddit',
    sections: [
      {
        heading: 'Create a Reddit script app',
        steps: [
          { text: 'Go to Reddit App Preferences (you must be logged into Reddit).', link: 'https://www.reddit.com/prefs/apps', linkLabel: 'Open Reddit Apps' },
          'Scroll to the bottom and click "Create App" (or "Create another app…").',
          'Fill in any name. Select type "script". Set redirect URI to http://localhost:8080 (this is required by Reddit but not actually used). Click Create App.',
        ],
      },
      {
        heading: 'Copy your credentials',
        steps: [
          'After creating the app, you\'ll see a box with your app. The Client ID is the short string directly under the app name (below "personal use script").',
          'The Client Secret is labeled "secret" in the same box.',
          'Username and Password are simply your Reddit account login — Reddit uses these for script-type apps.',
          'For User Agent, use this format: web:SocialixerApp:1.0 (by u/YourRedditUsername)',
        ],
      },
    ],
  },
};

// ─── Guide Modal ──────────────────────────────────────────────────────────────

function StepContent({ step, color }) {
  const isRich = typeof step === 'object';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.85rem', lineHeight: 1.65, whiteSpace: 'pre-line' }}>
        {isRich ? step.text : step}
      </Typography>
      {isRich && step.link && (
        <Box>
          <Button
            component="a"
            href={step.link}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
            sx={{
              px: 1.5,
              py: 0.4,
              minHeight: 0,
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '4px',
              color,
              bgcolor: alpha(color, 0.1),
              border: `1px solid ${alpha(color, 0.2)}`,
              '&:hover': { bgcolor: alpha(color, 0.18) },
            }}
          >
            {step.linkLabel || 'Open'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function PlatformGuideModal({ platform, open, onClose }) {
  const guide = PLATFORM_GUIDES[platform];
  const config = PLATFORM_CONFIG[platform];
  const icon = PLATFORM_ICONS[platform];
  if (!guide) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            background: 'rgba(15,15,20,0.97)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(20px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: alpha(config.color, 0.15), color: config.color, width: 36, height: 36 }}>
            {React.cloneElement(icon, { sx: { fontSize: 18 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
            {guide.title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.06)', py: 3 }}>
        {guide.sections.map((section, si) => (
          <Box key={si} sx={{ mb: si < guide.sections.length - 1 ? 3.5 : 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: config.color,
                mb: 1.5,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
              }}
            >
              Step {si + 1} — {section.heading}
            </Typography>
            <List dense disablePadding>
              {section.steps.map((step, idx) => (
                <ListItem key={idx} disablePadding sx={{ alignItems: 'flex-start', mb: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: alpha(config.color, 0.12),
                        color: config.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 900,
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={<StepContent step={step} color={config.color} />}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: '4px',
            fontWeight: 800,
            bgcolor: config.color,
            '&:hover': { bgcolor: alpha(config.color, 0.85) },
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Platform credential field definitions ───────────────────────────────────

const PLATFORM_FIELDS = {
  youtube: {
    note: 'Click "Connect to YouTube" below to authorize your YouTube channel. Your admin has already configured the OAuth app — no app credentials needed.',
    fields: [
      { key: 'channel_id', label: 'Channel ID (optional)', secret: false, placeholder: 'UCxxxxxxxxxxxxxxxxxxxx' },
    ],
  },
  instagram: {
    note: 'Click "Connect to Instagram" below to link your Instagram Business or Creator account. Your admin has already configured the OAuth app — no app credentials needed.',
    fields: [],
  },
  facebook: {
    note: 'Click "Connect to Facebook" below to link your Facebook Page. Your admin has already configured the OAuth app — no app credentials needed.',
    fields: [],
  },
  twitter: {
    note: 'Requires a Twitter Developer App with OAuth 1.0a User Context permissions. All five credentials are required.',
    fields: [
      { key: 'bearer_token', label: 'Bearer Token', secret: true },
      { key: 'api_key', label: 'API Key (Consumer Key)', secret: false },
      { key: 'api_secret', label: 'API Key Secret', secret: true },
      { key: 'access_token', label: 'Access Token', secret: false },
      { key: 'access_token_secret', label: 'Access Token Secret', secret: true },
    ],
  },
  linkedin: {
    note: 'Requires a LinkedIn OAuth 2.0 access token with r_liteprofile and w_member_social scopes.',
    fields: [
      { key: 'access_token', label: 'Access Token', secret: true },
    ],
  },
  reddit: {
    note: 'Create a "script" type app at reddit.com/prefs/apps. Use your Reddit account username and password for the script authentication.',
    fields: [
      { key: 'client_id', label: 'Client ID', secret: false },
      { key: 'client_secret', label: 'Client Secret', secret: true },
      { key: 'username', label: 'Reddit Username', secret: false },
      { key: 'password', label: 'Reddit Password', secret: true },
      { key: 'user_agent', label: 'User Agent', secret: false, placeholder: 'MyApp/1.0 by u/myusername' },
    ],
  },
};

// ─── Per-platform setup metadata ─────────────────────────────────────────────

const PLATFORM_SETUP_INFO = {
  youtube:   { setupTime: '~10 min', refreshNote: 'Refresh tokens never expire',       refreshDays: null },
  instagram: { setupTime: '~1 min',  refreshNote: 'Re-connect every 60 days',          refreshDays: 60   },
  facebook:  { setupTime: '~1 min',  refreshNote: 'Page token — re-connect every 60 days', refreshDays: 60 },
  twitter:   { setupTime: '~5 min',  refreshNote: 'Access tokens never expire',        refreshDays: null },
  linkedin:  { setupTime: '~5 min',  refreshNote: 'Re-connect every 60 days',          refreshDays: 60   },
  reddit:    { setupTime: '~3 min',  refreshNote: 'Script tokens never expire',        refreshDays: null },
};

// ─── Single platform section ──────────────────────────────────────────────────

function PlatformSection({ platform, savedCreds, onSave, connectedAccounts = [], accountLimit = {}, onDisconnect }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const config = PLATFORM_CONFIG[platform];
  const icon = PLATFORM_ICONS[platform];
  const fieldDefs = PLATFORM_FIELDS[platform];
  const setupInfo = PLATFORM_SETUP_INFO[platform] || {};

  const [values, setValues] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);

  // Populate from saved creds on mount / when savedCreds changes
  useEffect(() => {
    if (savedCreds) {
      const populated = {};
      fieldDefs.fields.forEach(({ key }) => {
        populated[key] = savedCreds[key] || '';
      });
      setValues(populated);
    }
  }, [savedCreds, fieldDefs.fields]);

  // OAuth platforms use connectedAccounts as source of truth for connection status.
  const isOAuthPlatform = ['instagram', 'facebook', 'youtube'].includes(platform);
  const hasAnySaved = isOAuthPlatform
    ? connectedAccounts.length > 0
    : (savedCreds && Object.values(savedCreds).some(Boolean));
  const allFieldsFilled = fieldDefs.fields.every(({ key }) => values[key]?.trim());

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(platform, values);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(`Are you sure you want to clear credentials for ${config.label}?`)) return;
    setSaving(true);
    try {
      await credentialsApi.delete(platform);
      setValues({});
      setSaved(false);
      window.location.reload();
    } catch (err) {
      setError(err.message || 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectYoutube = async () => {
    try {
      const data = await credentialsApi.getYouTubeAuthUrl();
      if (data && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setError(data?.error || 'Failed to initialize YouTube auth');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
  };

  const handleConnectFacebook = async () => {
    try {
      const data = await credentialsApi.getFacebookAuthUrl();
      if (data && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setError(data?.error || 'Failed to initialize Facebook auth');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
  };

  const handleConnectInstagram = async () => {
    try {
      const data = await credentialsApi.getInstagramAuthUrl();
      if (data && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setError(data?.error || 'Failed to initialize Instagram auth');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
  };

  return (
    <Accordion
      sx={{
        mb: 2,
        borderRadius: '16px !important',
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid',
        borderColor: hasAnySaved ? alpha(config.color, 0.3) : alpha(theme.palette.divider, 1),
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(20px)',
        '&:hover': {
          borderColor: alpha(config.color, 0.5),
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          background: hasAnySaved ? alpha(config.color, 0.08) : 'transparent',
          borderRadius: '16px',
          minHeight: 64,
          px: 3,
          '&.Mui-expanded': { borderRadius: '16px 16px 0 0' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1 }}>
          <Avatar sx={{ bgcolor: alpha(config.color, 0.1), color: config.color, width: 40, height: 40, border: `1px solid ${alpha(config.color, 0.2)}` }}>
            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {config.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {fieldDefs.fields.length > 0
                  ? `${fieldDefs.fields.length} parameter${fieldDefs.fields.length !== 1 ? 's' : ''}`
                  : 'OAuth connect'}
              </Typography>
              {setupInfo.setupTime && (
                <Tooltip title={`Estimated one-time setup time: ${setupInfo.setupTime}`} arrow>
                  <Chip
                    icon={<ScheduleIcon sx={{ fontSize: '11px !important' }} />}
                    label={`Setup ${setupInfo.setupTime}`}
                    size="small"
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.08), color: '#10b981', '& .MuiChip-icon': { color: '#10b981', ml: '4px' } }}
                  />
                </Tooltip>
              )}
              {setupInfo.refreshDays ? (
                <Tooltip title={`You'll need to reconnect this platform every ${setupInfo.refreshDays} days when the token expires`} arrow>
                  <Chip
                    icon={<AutorenewIcon sx={{ fontSize: '11px !important' }} />}
                    label={`Token expires every ${setupInfo.refreshDays}d`}
                    size="small"
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#f59e0b', 0.08), color: '#d97706', '& .MuiChip-icon': { color: '#d97706', ml: '4px' } }}
                  />
                </Tooltip>
              ) : setupInfo.setupTime ? (
                <Tooltip title="Once connected, your token never expires — no need to reconnect" arrow>
                  <Chip
                    icon={<TaskAltIcon sx={{ fontSize: '11px !important' }} />}
                    label="One-time setup"
                    size="small"
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#6366f1', 0.08), color: '#6366f1', '& .MuiChip-icon': { color: '#6366f1', ml: '4px' } }}
                  />
                </Tooltip>
              ) : null}
            </Box>
          </Box>
          <Box sx={{ mr: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {hasAnySaved ? (
              <>
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label={isOAuthPlatform ? `Connected (${connectedAccounts.length})` : 'Secured'}
                  size="small"
                  sx={{
                    background: alpha(config.color, 0.12),
                    color: config.color,
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': { color: config.color },
                  }}
                />
                {isOAuthPlatform && connectedAccounts.slice(0, 2).map((acct) => {
                  const name = acct.ig_username || acct.page_name || acct.channel_name || acct.display_name || acct.username;
                  return name ? (
                    <Typography key={acct.account_id} variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 600 }}>
                      @{name}
                    </Typography>
                  ) : null;
                })}
                {isOAuthPlatform && connectedAccounts.length > 2 && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                    +{connectedAccounts.length - 2} more
                  </Typography>
                )}
              </>
            ) : (
              <Chip
                label="Unconfigured"
                size="small"
                sx={{ color: 'text.disabled', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 4 }}>
        {/* ── Multi-account capability indicator (always visible for OAuth platforms) ─ */}
        {isOAuthPlatform && (
          <Box sx={{
            mb: 3, p: 2, borderRadius: '10px',
            bgcolor: alpha(config.color, isDark ? 0.08 : 0.04),
            border: `1px dashed ${alpha(config.color, 0.3)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '8px',
                bgcolor: alpha(config.color, 0.12), color: config.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {React.cloneElement(icon, { sx: { fontSize: 18 } })}
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Multi-account support
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {accountLimit.limit === -1
                    ? `Unlimited ${config.label} accounts on your plan`
                    : `Connect up to ${accountLimit.limit} ${config.label} account${accountLimit.limit !== 1 ? 's' : ''} on your plan`}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={`${connectedAccounts.length} / ${accountLimit.limit === -1 ? '∞' : accountLimit.limit}`}
              size="small"
              sx={{
                fontWeight: 800, fontSize: '0.72rem',
                bgcolor: accountLimit.can_add ? alpha('#10b981', 0.12) : alpha('#ef4444', 0.12),
                color: accountLimit.can_add ? '#10b981' : '#ef4444',
              }}
            />
          </Box>
        )}

        {/* ── Connected accounts list ───────────────────────────────── */}
        {connectedAccounts.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                Connected Accounts
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {connectedAccounts.map((acct) => (
                <Box
                  key={acct.account_id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 1.5, px: 2, borderRadius: '8px',
                    border: `1px solid ${alpha(config.color, 0.2)}`,
                    bgcolor: alpha(config.color, isDark ? 0.06 : 0.03),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981', flexShrink: 0 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {acct.display_name || acct.ig_username || acct.page_name || acct.channel_name || acct.username || 'Connected'}
                      </Typography>
                      {acct.ig_username && <Typography variant="caption" sx={{ color: 'text.secondary' }}>@{acct.ig_username}</Typography>}
                      {acct.page_name && !acct.ig_username && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{acct.page_name}</Typography>}
                      {acct.channel_name && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{acct.channel_name}</Typography>}
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    startIcon={disconnecting === acct.account_id ? <CircularProgress size={12} /> : <DisconnectIcon sx={{ fontSize: 14 }} />}
                    disabled={disconnecting === acct.account_id}
                    onClick={async () => {
                      if (!window.confirm(`Disconnect ${acct.display_name || 'this account'}?`)) return;
                      setDisconnecting(acct.account_id);
                      try { await onDisconnect(acct.account_id); }
                      catch (e) { setError(e.message || 'Failed to disconnect'); }
                      finally { setDisconnecting(null); }
                    }}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>
            {!accountLimit.can_add && (
              <Alert
                severity="warning"
                icon={<UpgradeIcon fontSize="small" />}
                sx={{ mt: 2, borderRadius: '6px', fontSize: '0.82rem' }}
              >
                You've reached your plan's account limit. Upgrade to connect more accounts.
              </Alert>
            )}
          </Box>
        )}

        <Alert
          severity="info"
          icon={<InfoIcon fontSize="small" />}
          sx={{ mb: 4, borderRadius: '6px', fontSize: '0.85rem', bgcolor: alpha('#6366f1', isDark ? 0.1 : 0.05), color: isDark ? '#a5b4fc' : '#4338ca', border: `1px solid ${alpha('#6366f1', 0.15)}` }}
          action={
            PLATFORM_GUIDES[platform] && (
              <Button
                size="small"
                startIcon={<HelpIcon fontSize="small" />}
                onClick={() => setGuideOpen(true)}
                sx={{ fontWeight: 700, textTransform: 'none', color: isDark ? '#a5b4fc' : '#4338ca', whiteSpace: 'nowrap' }}
              >
                How to get these
              </Button>
            )
          }
        >
          {fieldDefs.note}
        </Alert>

        <PlatformGuideModal platform={platform} open={guideOpen} onClose={() => setGuideOpen(false)} />

        {fieldDefs.fields.length > 0 && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {fieldDefs.fields.map(({ key, label, secret, placeholder }) => (
                <TextField
                  key={key}
                  fullWidth
                  label={label}
                  placeholder={placeholder || (secret ? '••••••••••••' : `Enter ${label}`)}
                  value={values[key] || ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  type={secret && !showSecret[key] ? 'password' : 'text'}
                  slotProps={secret ? {
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowSecret((prev) => ({ ...prev, [key]: !prev[key] }))}
                            edge="end"
                          >
                            {showSecret[key] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  } : undefined}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                />
              ))}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: '6px' }}>{error}</Alert>
            )}

            <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                data-testid="save-credentials-button"
                disabled={saving || !allFieldsFilled}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : saved ? <CheckCircleIcon /> : <SaveIcon />}
                sx={{
                  borderRadius: '4px',
                  px: 4,
                  py: 1.25,
                  fontWeight: 800,
                  background: saved
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : `linear-gradient(135deg, ${config.color}, ${alpha(config.color, 0.8)})`,
                  boxShadow: `0 4px 14px ${alpha(config.color, 0.35)}`,
                }}
              >
                {saving ? 'Syncing...' : saved ? 'Success' : 'Secure Credentials'}
              </Button>

              {hasAnySaved && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClear}
                  data-testid="reset-credentials-button"
                  disabled={saving}
                  startIcon={<DeleteIcon />}
                  sx={{ borderRadius: '4px', fontWeight: 800, px: 3 }}
                >
                  Reset
                </Button>
              )}
            </Box>
          </>
        )}

        {/* Zero-knowledge reassurance — shown after first save */}
        {saved && (
          <Box sx={{
            mt: 2.5, p: 2, borderRadius: '6px',
            bgcolor: isDark ? alpha('#10b981', 0.08) : alpha('#10b981', 0.05),
            border: `1px solid ${alpha('#10b981', 0.2)}`,
            display: 'flex', alignItems: 'flex-start', gap: 1.5,
          }}>
            <LockIcon sx={{ color: '#10b981', fontSize: 18, mt: 0.1, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#10b981', display: 'block', mb: 0.25 }}>
                Your data is yours — always.
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                Stored encrypted with per-user Firebase security rules. Even our team cannot read your credentials. You can reset them instantly at any time.
                {setupInfo.refreshNote && (
                  <> &nbsp;·&nbsp; <strong>{setupInfo.refreshNote}</strong>.</>
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {platform === 'youtube' && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
              OAuth Connection
            </Typography>
            {savedCreds?.refresh_token ? (
              <Alert severity="success" sx={{ borderRadius: '6px', alignItems: 'center' }}>
                Successfully connected to YouTube!
              </Alert>
            ) : null}
            <Button
              variant={connectedAccounts.length > 0 ? 'outlined' : 'contained'}
              onClick={handleConnectYoutube}
              disabled={!accountLimit.can_add && connectedAccounts.length > 0}
              startIcon={connectedAccounts.length > 0 ? <AddIcon /> : null}
              sx={{
                mt: connectedAccounts.length > 0 ? 1.5 : 0,
                borderRadius: '4px', px: 4, py: 1.25, fontWeight: 800,
                ...(connectedAccounts.length === 0 && { bgcolor: '#ff0000', color: '#fff', '&:hover': { bgcolor: '#cc0000' } }),
              }}
            >
              {connectedAccounts.length > 0 ? 'Add Another YouTube Account' : 'Connect to YouTube'}
            </Button>
          </Box>
        )}

        {platform === 'instagram' && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
              OAuth Connection
            </Typography>
            <Button
              variant={connectedAccounts.length > 0 ? 'outlined' : 'contained'}
              onClick={handleConnectInstagram}
              disabled={!accountLimit.can_add && connectedAccounts.length > 0}
              startIcon={connectedAccounts.length > 0 ? <AddIcon /> : null}
              sx={{
                borderRadius: '4px', px: 4, py: 1.25, fontWeight: 800,
                ...(connectedAccounts.length === 0 && {
                  background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)',
                  color: '#fff', '&:hover': { opacity: 0.9 },
                }),
              }}
            >
              {connectedAccounts.length > 0 ? 'Add Another Instagram Account' : 'Connect to Instagram'}
            </Button>
          </Box>
        )}

        {platform === 'facebook' && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
              OAuth Connection
            </Typography>
            <Button
              variant={connectedAccounts.length > 0 ? 'outlined' : 'contained'}
              onClick={handleConnectFacebook}
              disabled={!accountLimit.can_add && connectedAccounts.length > 0}
              startIcon={connectedAccounts.length > 0 ? <AddIcon /> : null}
              sx={{
                borderRadius: '4px', px: 4, py: 1.25, fontWeight: 800,
                ...(connectedAccounts.length === 0 && { bgcolor: '#1877F2', color: '#fff', '&:hover': { bgcolor: '#1565c0' } }),
              }}
            >
              {connectedAccounts.length > 0 ? 'Add Another Facebook Page' : 'Connect to Facebook'}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Main Settings page ───────────────────────────────────────────────────────

const PREF_TONES = ['Professional', 'Casual', 'Funny', 'Inspirational', 'Educational', 'Bold/Controversial'];
const PREF_EMOJI = ['Never', 'Occasionally', 'Frequently', 'Every Post'];
const PREF_PLATFORMS = ['Instagram', 'YouTube', 'TikTok', 'LinkedIn', 'Twitter', 'Facebook', 'Reddit'];

// eslint-disable-next-line no-unused-vars
function ContentPreferencesSection({ isDark, theme }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [topicInput, setTopicInput] = useState('');

  useEffect(() => {
    onboardingApi.get().then((res) => {
      if (res?.onboarding) setPrefs(res.onboarding);
      else setPrefs({});
    }).catch(() => setPrefs({}));
  }, []);

  const set = (field, value) => setPrefs((prev) => ({ ...prev, [field]: value }));

  const toggle = (field, option, multi = false) => {
    if (multi) {
      const arr = (prefs?.[field] || []);
      set(field, arr.includes(option) ? arr.filter((v) => v !== option) : [...arr, option]);
    } else {
      set(field, prefs?.[field] === option ? '' : option);
    }
  };

  const addTopic = () => {
    const t = topicInput.trim().toLowerCase();
    if (t && !(prefs?.audience_topics || []).includes(t)) {
      set('audience_topics', [...(prefs?.audience_topics || []), t]);
    }
    setTopicInput('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onboardingApi.update(prefs, true);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (_) {}
    setSaving(false);
  };

  if (prefs === null) return <CircularProgress size={24} />;

  return (
    <Box sx={{ maxWidth: 700 }}>
      {/* Niche */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Content Niche</Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. fitness & wellness, SaaS marketing…"
          value={prefs.niche || ''}
          onChange={(e) => set('niche', e.target.value)}
        />
      </Box>

      {/* Tone */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Content Tone</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PREF_TONES.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              onClick={() => toggle('tone', opt)}
              sx={{
                cursor: 'pointer',
                fontWeight: prefs.tone === opt ? 700 : 400,
                bgcolor: prefs.tone === opt ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                border: `1.5px solid ${prefs.tone === opt ? theme.palette.primary.main : theme.palette.divider}`,
                color: prefs.tone === opt ? 'primary.main' : 'text.secondary',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Emoji usage */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Emoji Usage</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PREF_EMOJI.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              onClick={() => toggle('emoji_usage', opt)}
              sx={{
                cursor: 'pointer',
                fontWeight: prefs.emoji_usage === opt ? 700 : 400,
                bgcolor: prefs.emoji_usage === opt ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                border: `1.5px solid ${prefs.emoji_usage === opt ? theme.palette.primary.main : theme.palette.divider}`,
                color: prefs.emoji_usage === opt ? 'primary.main' : 'text.secondary',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Platforms */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Active Platforms</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PREF_PLATFORMS.map((opt) => {
            const active = (prefs.platforms || []).includes(opt);
            return (
              <Chip
                key={opt}
                label={opt}
                onClick={() => toggle('platforms', opt, true)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: active ? 700 : 400,
                  bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                  border: `1.5px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
                  color: active ? 'primary.main' : 'text.secondary',
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Audience topics */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Audience Topics</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            size="small"
            placeholder="Add a topic…"
            value={topicInput}
            onChange={(e) => setTopicInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="outlined" size="small" onClick={addTopic}>Add</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(prefs.audience_topics || []).map((t) => (
            <Chip
              key={t}
              label={t}
              onDelete={() => set('audience_topics', (prefs.audience_topics || []).filter((x) => x !== t))}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}
            />
          ))}
        </Box>
      </Box>

      {/* Target audience */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Target Audience</Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. busy professionals aged 25–40"
          value={prefs.target_audience || ''}
          onChange={(e) => set('target_audience', e.target.value)}
        />
      </Box>

      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ minWidth: 140 }}
      >
        {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Preferences'}
      </Button>
    </Box>
  );
}

const Settings = React.memo(() => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [credentials, setCredentials] = useState({});
  const [socialAccounts, setSocialAccounts] = useState({ accounts: [], current: 0, limit: 1, can_add: true });
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState('');
  const [errorSnackOpen, setErrorSnackOpen] = useState(false);
  const [pageIdPrompt, setPageIdPrompt] = useState(false);
  const [pageIdInput, setPageIdInput] = useState('');
  const [pageIdLoading, setPageIdLoading] = useState(false);
  const [fbPageIdPrompt, setFbPageIdPrompt] = useState(false);
  const [fbPageIdInput, setFbPageIdInput] = useState('');
  const [fbPageIdLoading, setFbPageIdLoading] = useState(false);

  const showError = (msg) => {
    const friendly = typeof msg === 'string' && msg.startsWith('400 ')
      ? 'A server configuration error occurred. Please try again or contact support.'
      : (msg || 'Something went wrong. Please try again.');
    setGlobalError(friendly);
    setErrorSnackOpen(true);
  };
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [waitlistMode, setWaitlistMode] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');
  const { getAllowedPlatforms } = useFeatureToggles();

  // Show a platform in Settings if it's enabled in ANY platform feature
  const allowedPlatforms = [...new Set(
    PLATFORM_FEATURES.flatMap(feat => getAllowedPlatforms(feat))
  )];
  const visiblePlatforms = Object.keys(PLATFORM_FIELDS).filter(p => allowedPlatforms.includes(p));

  useEffect(() => {
    (async () => {
      // Check super_admin claim
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdTokenResult();
          if (token.claims.super_admin) {
            setIsSuperAdmin(true);
            const isWaitlist = await featuresApi.getWaitlistMode();
            setWaitlistMode(isWaitlist);
          }
        } catch (_) {}
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [creds, acctData] = await Promise.all([
          credentialsApi.getAll(),
          socialAccountsApi.list().catch(() => ({ accounts: [], current: 0, limit: 1, can_add: true })),
        ]);
        setCredentials(creds || {});
        setSocialAccounts(acctData);

        // Handle OAuth callbacks (YouTube and Facebook both redirect back here)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        if (code) {
          // Clear URL immediately to prevent React Strict Mode double-fire
          window.history.replaceState({}, document.title, window.location.pathname);

          setLoading(true);
          // Route by state prefix: fb__ = Facebook, ig__ = Instagram, else YouTube
          const isFacebook  = state && state.startsWith('fb__');
          const isInstagram = state && state.startsWith('ig__');
          try {
            let data;
            if (isFacebook) {
              data = await credentialsApi.handleFacebookCallback(code, state);
            } else if (isInstagram) {
              data = await credentialsApi.handleInstagramCallback(code, state);
            } else {
              data = await credentialsApi.handleYouTubeCallback(code, state);
            }
            if (data && data.success) {
              const [updatedCreds, updatedAccts] = await Promise.all([
                credentialsApi.getAll(),
                socialAccountsApi.list().catch(() => []),
              ]);
              setCredentials(updatedCreds || {});
              setSocialAccounts(updatedAccts);
            } else if (data?.upgrade_required) {
              showError(data.error || 'Account limit reached. Please upgrade your plan.');
            } else {
              const platform = isFacebook ? 'Facebook' : isInstagram ? 'Instagram' : 'YouTube';
              showError(data?.error || `Failed to connect ${platform}`);
            }
          } catch (callbackErr) {
            if (callbackErr.needsPageId && isInstagram) {
              setPageIdPrompt(true);
            } else if (callbackErr.needsPageId && isFacebook) {
              setFbPageIdPrompt(true);
            } else {
              showError(callbackErr.message || 'Failed to handle OAuth callback');
            }
          }
        }
      } catch (err) {
        showError('Could not load saved credentials. Verify Firebase connectivity.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = useCallback(async (platform, values) => {
    await credentialsApi.save(platform, values);
    setCredentials((prev) => ({ ...prev, [platform]: values }));
  }, []);

  const handleWaitlistToggle = async (e) => {
    const newState = e.target.checked;
    setWaitlistLoading(true);
    setWaitlistError('');
    try {
      await adminApi.setWaitlistMode(newState);
      setWaitlistMode(newState);
    } catch (err) {
      setWaitlistError(err.message || 'Failed to update waitlist mode');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const configuredCount = visiblePlatforms.filter(
    (p) => credentials[p] && Object.values(credentials[p]).some(Boolean)
  ).length;

  return (
    <Box>
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
            Platform Settings
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Connect your social empire. Your credentials are encrypted and stored securely.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip
            label={`${configuredCount} / ${visiblePlatforms.length} Linked`}
            sx={{ fontWeight: 900, px: 1, height: 40, borderRadius: '4px', background: alpha('#6366f1', 0.1), color: '#6366f1' }}
          />
        </Box>
      </Box>

      {/* Facebook Page ID manual fallback dialog */}
      <Dialog open={fbPageIdPrompt} onClose={() => setFbPageIdPrompt(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Enter Your Facebook Page ID</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Your Facebook Page wasn't returned during OAuth. Enter your Page ID to connect manually.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Your Socialixer Page ID is <strong>1106606002532234</strong> — or find it in your Page URL / About section.
          </Typography>
          <TextField
            fullWidth
            label="Facebook Page ID"
            placeholder="e.g. 1106606002532234"
            value={fbPageIdInput}
            onChange={(e) => setFbPageIdInput(e.target.value)}
            sx={{ mt: 1 }}
          />
          {fbPageIdLoading && <Box sx={{ mt: 1 }}><CircularProgress size={20} /></Box>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFbPageIdPrompt(false)} disabled={fbPageIdLoading}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!fbPageIdInput.trim() || fbPageIdLoading}
            onClick={async () => {
              setFbPageIdLoading(true);
              try {
                const data = await credentialsApi.resolveFacebookPage(fbPageIdInput.trim());
                if (data?.success) {
                  setFbPageIdPrompt(false);
                  setFbPageIdInput('');
                  const [updatedCreds, updatedAccts] = await Promise.all([
                    credentialsApi.getAll(),
                    socialAccountsApi.list().catch(() => []),
                  ]);
                  setCredentials(updatedCreds || {});
                  setSocialAccounts(updatedAccts);
                } else {
                  showError(data?.error || 'Could not link Facebook Page.');
                  setFbPageIdPrompt(false);
                }
              } catch (err) {
                showError(err.message || 'Failed to resolve page');
                setFbPageIdPrompt(false);
              } finally {
                setFbPageIdLoading(false);
              }
            }}
          >
            Connect Facebook
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instagram Page ID manual fallback dialog */}
      <Dialog open={pageIdPrompt} onClose={() => setPageIdPrompt(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Enter Your Facebook Page ID</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Your Facebook Page wasn't returned during OAuth. Enter your Page ID manually so we can link your Instagram account.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Find your Page ID: go to your <strong>Facebook Page</strong> → <strong>About</strong> → scroll to the bottom — it shows a numeric Page ID. Or copy it from the page URL.
          </Typography>
          <TextField
            fullWidth
            label="Facebook Page ID"
            placeholder="e.g. 123456789012345"
            value={pageIdInput}
            onChange={(e) => setPageIdInput(e.target.value)}
            sx={{ mt: 1 }}
          />
          {pageIdLoading && <Box sx={{ mt: 1 }}><CircularProgress size={20} /></Box>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPageIdPrompt(false)} disabled={pageIdLoading}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pageIdInput.trim() || pageIdLoading}
            onClick={async () => {
              setPageIdLoading(true);
              try {
                const data = await credentialsApi.resolveInstagramPage(pageIdInput.trim());
                if (data?.success) {
                  setPageIdPrompt(false);
                  setPageIdInput('');
                  const [updatedCreds, updatedAccts] = await Promise.all([
                    credentialsApi.getAll(),
                    socialAccountsApi.list().catch(() => []),
                  ]);
                  setCredentials(updatedCreds || {});
                  setSocialAccounts(updatedAccts);
                } else {
                  showError(data?.error || 'Could not link Instagram account.');
                  setPageIdPrompt(false);
                }
              } catch (err) {
                showError(err.message || 'Failed to resolve page');
                setPageIdPrompt(false);
              } finally {
                setPageIdLoading(false);
              }
            }}
          >
            Connect Instagram
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={errorSnackOpen}
        autoHideDuration={8000}
        onClose={() => setErrorSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorSnackOpen(false)}
          sx={{ borderRadius: '8px', maxWidth: 520 }}
        >
          {globalError}
        </Alert>
      </Snackbar>

      <Card
        elevation={0}
        sx={{
          borderRadius: '16px',
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(30px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: '0 20px 50px -20px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 3 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.secondary' }}>Loading Safely...</Typography>
              </Box>
            ) : (
              <>
                {/* ── Trust pillars ── */}
                <Box sx={{ mb: 5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: alpha('#10b981', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <LockIcon sx={{ color: '#10b981', fontSize: 18 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>How we protect your credentials</Typography>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                    {[
                      {
                        icon: <LockIcon sx={{ fontSize: 20, color: '#10b981' }} />,
                        color: '#10b981',
                        title: 'Zero-Knowledge Storage',
                        body: 'Your credentials are encrypted with per-user Firebase security rules. Our team cannot read them — ever.',
                      },
                      {
                        icon: <VerifiedUserIcon sx={{ fontSize: 20, color: '#6366f1' }} />,
                        color: '#6366f1',
                        title: 'Full Control, Revoke Instantly',
                        body: 'Hit Reset at any time to wipe your credentials immediately. No lock-in. Works in under a second.',
                      },
                      {
                        icon: <ScheduleIcon sx={{ fontSize: 20, color: '#f59e0b' }} />,
                        color: '#f59e0b',
                        title: 'Used Only When Posting',
                        body: 'Credentials are accessed exclusively during scheduled post execution — never logged, never shared.',
                      },
                    ].map(({ icon, color, title, body }) => (
                      <Box key={title} sx={{
                        p: 2.5, borderRadius: '6px',
                        bgcolor: isDark ? alpha(color, 0.06) : alpha(color, 0.04),
                        border: `1px solid ${alpha(color, 0.15)}`,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {icon}
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color, fontSize: '0.8rem' }}>{title}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.8rem' }}>{body}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ mb: 5, opacity: 0.5 }} />

                {isSuperAdmin && <DeveloperInfo />}

                <Box sx={{ maxWidth: 900 }}>
                  {visiblePlatforms.map((platform) => (
                    <PlatformSection
                      key={platform}
                      platform={platform}
                      savedCreds={credentials[platform]}
                      onSave={handleSave}
                      connectedAccounts={socialAccounts.accounts.filter(a => a.platform === platform)}
                      accountLimit={socialAccounts}
                      onDisconnect={async (accountId) => {
                        await socialAccountsApi.remove(accountId);
                        const updated = await socialAccountsApi.list();
                        setSocialAccounts(updated);
                      }}
                    />
                  ))}
                </Box>

                {isSuperAdmin && (
                  <>
                    <Divider sx={{ my: 5, opacity: 0.5 }} />
                    <Box sx={{ maxWidth: 900 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <AdminPanelSettingsIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>Super Admin</Typography>
                      </Box>
                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: '6px',
                          background: alpha('#6366f1', isDark ? 0.06 : 0.03),
                          border: `1px solid ${alpha('#6366f1', 0.15)}`,
                          p: 3,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Waitlist Mode
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          When enabled, the public landing page shows a waitlist signup instead of the app login and pricing.
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={waitlistMode}
                              onChange={handleWaitlistToggle}
                              disabled={waitlistLoading}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {waitlistMode ? 'Waitlist mode ON' : 'Waitlist mode OFF'}
                            </Typography>
                          }
                        />
                        {waitlistError && (
                          <Alert severity="error" sx={{ mt: 2, borderRadius: '6px' }}>{waitlistError}</Alert>
                        )}
                      </Card>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

export default Settings;
